import { useState, useCallback } from 'react';
import { skillsAPI } from '@/services/api';
import type { LicenseSkillAssignment } from '../types';
import type { Skill } from '@/types/domain';

interface UseLicenseSkillsReturn {
  availableSkills: LicenseSkillAssignment[];
  selectedSkills: string[];
  loading: boolean;
  loadForCreate: () => Promise<void>;
  loadForLicense: (licenseKey: string) => Promise<void>;
  toggleSkill: (skillId: string, checked: boolean) => void;
  setSelectedSkills: (ids: string[]) => void;
  reset: () => void;
  updateLicenseSkills: (licenseKey: string) => Promise<void>;
}

/**
 * 技能关联管理 Hook
 */
export function useLicenseSkills(): UseLicenseSkillsReturn {
  const [availableSkills, setAvailableSkills] = useState<LicenseSkillAssignment[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const mapSkillToAssignment = (skill: Skill): LicenseSkillAssignment => ({
    id: skill.id,
    name: skill.name,
    category: skill.category,
    enabled: false,
  });

  const loadForCreate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await skillsAPI.getAll();
      const skills = response.skills || [];
      setAvailableSkills(skills.map(mapSkillToAssignment));
      setSelectedSkills([]);
    } catch (requestError) {
      console.error('Failed to load skills:', requestError);
      setAvailableSkills([]);
      setSelectedSkills([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadForLicense = useCallback(async (licenseKey: string) => {
    setLoading(true);
    try {
      const response = await skillsAPI.getLicenseSkills(licenseKey);
      const skills: LicenseSkillAssignment[] = response.data || [];
      setAvailableSkills(skills);
      setSelectedSkills(skills.filter((skill) => skill.enabled).map((skill) => skill.id));
    } catch (requestError) {
      console.error('Failed to load license skills:', requestError);
      setAvailableSkills([]);
      setSelectedSkills([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleSkill = useCallback((skillId: string, checked: boolean) => {
    setSelectedSkills((previous) => {
      if (checked && !previous.includes(skillId)) {
        return [...previous, skillId];
      }
      if (!checked) {
        return previous.filter((id) => id !== skillId);
      }
      return previous;
    });
  }, []);

  const reset = useCallback(() => {
    setAvailableSkills([]);
    setSelectedSkills([]);
  }, []);

  const updateLicenseSkills = useCallback(async (licenseKey: string) => {
    await skillsAPI.updateLicenseSkills(licenseKey, selectedSkills);
  }, [selectedSkills]);

  return {
    availableSkills,
    selectedSkills,
    loading,
    loadForCreate,
    loadForLicense,
    toggleSkill,
    setSelectedSkills,
    reset,
    updateLicenseSkills,
  };
}
